import React from "react";
import { BookOpen, Shield, GraduationCap, Briefcase } from "lucide-react";
import { Button } from "../ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "../ui/carousel";
import Autoplay from "embla-carousel-autoplay";

const chunk = (arr, size) =>
    arr.length ? [arr.slice(0, size), ...chunk(arr.slice(size), size)] : [];

const safeScrollToId = (id) => {
    if (typeof document === "undefined") return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const CompetenceAndTopicsSection = () => {
    const [api, setApi] = React.useState(null);
    const [current, setCurrent] = React.useState(0);

    React.useEffect(() => {
        if (!api) return;
        setCurrent(api.selectedScrollSnap());
        api.on("select", () => setCurrent(api.selectedScrollSnap()));
    }, [api]);
    const QUALITIES = [
        {
            icon: Briefcase,
            title: "Корпоративный опыт и понимание проблемы",
            desc: "В отличие от многих психологов, я понимаю корпоративный мир изнутри. Конфликты интересов и борьба за ресурсы мне знакомы не понаслышке.",
        },
        {
            icon: GraduationCap,
            title: "Профессионал с высшим профильным образованием",
            desc: "Фундаментальные знания по психологии получены в БГУ (г. Минск, очно). Дополнительное образование регулярно повышает мою специализацию в методах.",
        },
        {
            icon: BookOpen,
            title: "Когнитивно-поведенческая терапия",
            desc: "КПТ помогает справляться со стрессом на работе и в жизни, не зацикливаясь на прошлом. Мы сосредотачиваемся на том, что мешает вашей эффективности сегодня.",
        },
        {
            icon: Shield,
            title: "Совершенствование профессиональных знаний",
            desc: "Регулярно консультируюсь с опытными коллегами, что позволяет поддерживать высокий уровень профессионализма и объективный взгляд. Это помогает мне сохранить ресурсное состояние.",
        },
    ];

    const WORK_TOPICS = [
        "Тревожные расстройства",
        "Панические атаки",
        "Страхи, фобии",
        "Депрессивные расстройства",
        "Подавленность, апатия",
        "Навязчивые состояния",
        "Синдром отложенной жизни",
        "Эмоциональное выгорание",
        "Прокрастинация",
        "Чувство вины",
        "Бессонница",
        "Проблемы с коммуникацией",
        "Психосоматические проблемы",
        "Проблемы самооценки",
        "Проблемы с пищевым поведением",
        "Отношения в паре",
        "Семейные конфликты",
        "Выход из токсичных отношений и созависимости",
        "Переживание расставания",
        "Столкновение с изменой партнера",
        "Сепарация от родителей",
        "Самореализация",
        "Кризис самоопределения",
        "Неустойчивая или противоречивая идентичность",
        "Возрастные кризисы",
        "Горе. Переживание утраты. Потеря близкого человека",
        "Перинатальные утраты",
        "Травма",
        "Посттравматическое стрессовое расстройство (ПТСР)",
        "Комплексное посттравматическое стрессовое расстройство (КПТСР)",
        "Расстройства личности",
    ];
    const totalSlides = Math.ceil(WORK_TOPICS.length / 8);

    return (
        <section id="competence" className="bg-white relative overflow-hidden">
            <div
                className="absolute z-0 left-[3%] top-0 sm:top-[5%] overflow-hidden md:top-[-10%] w-[90vw] sm:w-[90vw] md:w-[40vw] ml-[5vw] h-[60vh] bg-no-repeat bg-bottom bg-contain pointer-events-none"
                style={{ backgroundImage: "url('/image/svg/vetka-3.svg')" }}
            />

            {/* Part 1: "Поддержка вас..." */}
            <div className="relative z-5 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16">
                <div className="grid md:grid-cols-4 gap-8 pb-12 border-b border-olive-200">
                    <h2 className="font-serif pt-32 text-3xl text-olive-950">
                        Поддержка вас в преодолении трудностей
                    </h2>

                    <div className="md:col-span-3 grid sm:grid-cols-2 gap-8">
                        {QUALITIES.map((q, index) => (
                            <div key={index} className="space-y-1">
                                <h3 className="font-serif font-semibold leading-none tracking-tight text-olive-950">{q.title}</h3>
                                <p className="text-olive-900/70 text-sm">{q.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Part 2: "Вместе решим..." */}
            <div
                className="bg-olive-50 w-full py-16 relative"
                style={{
                    backgroundImage: `radial-gradient(circle at 1px 1px, hsla(120, 10%, 80%, 0.7) 1px, transparent 0)`,
                    backgroundSize: `20px 20px`,
                }}
            >
                <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 relative z-10 bg-olive-50 py-8 rounded-lg">
                    <h2 className="font-serif text-3xl text-olive-950 mb-8">
                        Вместе решим ключевые личностные и профессиональные проблемы
                    </h2>

                    <Carousel className="w-full" setApi={setApi} plugins={[Autoplay({ delay: 3000, stopOnMouseEnter: true, stopOnInteraction: true })]}>
                        <CarouselContent>
                            {chunk(WORK_TOPICS, 8).map((group, pageIndex) => (
                                <CarouselItem key={pageIndex}>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                                        {group.map((topic, index) => (
                                            <div key={index} className="flex items-center justify-between border-b border-olive-200 pb-4">
                                                <span className="text-sm text-olive-900">{topic}</span>
                                                <Button className="bg-white text-olive-800 duration-300 hover:scale-105 border border-olive-200 rounded-2xl hover:bg-olive-100 shrink-0 ml-2" onClick={() => safeScrollToId("booking")}>
                                                    Обсудить проблему
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </CarouselItem>
                            ))}
                        </CarouselContent>
                        <div className="flex items-center justify-center gap-4 mt-6">
                            <CarouselPrevious className="static translate-y-0" />
                            <div className="flex items-center gap-2">
                                {Array.from({ length: totalSlides }).map((_, i) => (
                                    <button
                                        key={i}
                                        className={`w-2 h-2 rounded-full transition-colors ${i === current ? "bg-olive-700" : "bg-olive-300"}`}
                                        onClick={() => api?.scrollTo(i)}
                                    />
                                ))}
                            </div>
                            <CarouselNext className="static translate-y-0" />
                        </div>
                    </Carousel>
                </div>
            </div>
        </section>
    );
};
